import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import type { AgentDirective, DirectiveScope } from './types';
import { DIRECTIVE_SCOPES } from './types';

interface DirectiveModalProps {
  directive: AgentDirective | null;
  onClose: () => void;
  onSaved: () => void;
}

const DirectiveModal: React.FC<DirectiveModalProps> = ({ directive, onClose, onSaved }) => {
  const isEdit = Boolean(directive?.id);
  const [key, setKey] = useState(directive?.key ?? '');
  const [scope, setScope] = useState<DirectiveScope>(directive?.scope ?? 'global');
  const [directiveText, setDirectiveText] = useState(directive?.directive ?? '');
  const [rationale, setRationale] = useState(directive?.rationale ?? '');
  const [priority, setPriority] = useState(directive?.priority ?? 100);
  const [appliesTo, setAppliesTo] = useState(
    (directive?.applies_to ?? []).join(', '),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (directive) {
      setKey(directive.key);
      setScope(directive.scope);
      setDirectiveText(directive.directive);
      setRationale(directive.rationale ?? '');
      setPriority(directive.priority);
      setAppliesTo((directive.applies_to ?? []).join(', '));
    }
  }, [directive]);

  const handleSave = async () => {
    if (!key.trim() || !directiveText.trim()) {
      toast.error('Key and directive are required');
      return;
    }

    setSaving(true);
    try {
      const applies_to = appliesTo
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (isEdit && directive) {
        const { error: revokeError } = await supabase
          .from('agent_directives')
          .update({ status: 'superseded', updated_at: new Date().toISOString() })
          .eq('id', directive.id);
        if (revokeError) throw revokeError;
      }

      const { error } = await supabase.from('agent_directives').insert({
        key: key.trim(),
        scope,
        directive: directiveText.trim(),
        rationale: rationale.trim() || null,
        status: 'active',
        priority,
        applies_to,
        examples: directive?.examples ?? {},
        metadata: directive?.metadata ?? {},
        created_via: 'engine_ui',
      });

      if (error) throw error;
      toast.success(isEdit ? 'Directive updated (new version)' : 'Directive created');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit directive' : 'New directive'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Key
              </label>
              <input
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                disabled={isEdit}
                placeholder="datetime.user_facing"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Scope
              </label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as DirectiveScope)}
                disabled={isEdit}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {DIRECTIVE_SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Directive
            </label>
            <textarea
              value={directiveText}
              onChange={(e) => setDirectiveText(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rationale (optional)
            </label>
            <input
              type="text"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority (lower = higher)
              </label>
              <input
                type="number"
                min={1}
                max={999}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Applies to tools (comma-separated)
              </label>
              <input
                type="text"
                value={appliesTo}
                onChange={(e) => setAppliesTo(e.target.value)}
                placeholder="engine_get_build_info"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {isEdit && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Saving creates a new active version and marks the current row superseded (history preserved).
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectiveModal;
